import { useMemo } from 'react';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { useMessages } from '@/hooks/messages';
import type {
  CostEstimationItemsTableRecord,
  CostModel,
  ResourceEstimationItemsTableRecord,
} from '@/pages/estimation/detail/model';

import HierarchicalManagementReportTable, {
  type HierarchicalTableRow,
} from '../components/HierarchicalManagementReportTable';
import { managementReportSummaryTableMessageDefinitions } from '../ManagementReportSummaryTable/messages';

export interface ManagementReportDirectCostTableProps {
  resourceEstimationItems: ResourceEstimationItemsTableRecord[];
  costEstimationItems: CostEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  getResourceEstimationItemsTableRecordsByParent: (
    parentId: string | undefined,
  ) => ResourceEstimationItemsTableRecord[];
  getCostEstimationItemsTableRecordsByParent: (
    parentId: string | undefined,
  ) => CostEstimationItemsTableRecord[];
  assumptions?: Array<{
    id: string;
    estimationItemId: string;
    description: string;
    isValid: boolean;
  }>;
  searchText?: string;
  hiddenCostTypeColumns?: string[];
}

const ManagementReportDirectCostTable: React.FC<ManagementReportDirectCostTableProps> = ({
  costEstimationItems = [],
  estimationModel,
  costs,
  getCostEstimationItemsTableRecordsByParent,
  assumptions = [],
  searchText = '',
  hiddenCostTypeColumns = [],
}: ManagementReportDirectCostTableProps) => {
  const messages = useMessages(managementReportSummaryTableMessageDefinitions);

  const tableConfig = useMemo(
    () => ({
      items: costEstimationItems,
      getChildrenByParent: getCostEstimationItemsTableRecordsByParent,
      createRowFromItem: (
        item: CostEstimationItemsTableRecord,
        _counter: number,
        subCounter?: number,
        level: number = 0,
      ) => ({
        id: subCounter ? `cost-sub-${item.id}` : `cost-${item.id}`,
        reference: item.reference,
        name: item.name,
        description: item.description,
        isParent: false,
        level,
        estimationItemId: item.id,
        costEstimationItem: item,
      }),
      getEstimationItemForVisibility: (row: HierarchicalTableRow) => row.costEstimationItem,
      defaultGroupName: messages.directCostItem(),
      showEffortsColumn: false, // Direct cost table doesn't show efforts
      emptyStateMessage: messages.noDirectCostItems(),
    }),
    [costEstimationItems, getCostEstimationItemsTableRecordsByParent, messages],
  );

  return (
    <HierarchicalManagementReportTable
      config={tableConfig}
      costs={costs}
      estimationModel={estimationModel}
      assumptions={assumptions}
      searchText={searchText}
      hiddenCostTypeColumns={hiddenCostTypeColumns}
    />
  );
};

export default ManagementReportDirectCostTable;
